import type { CaseStudy } from '../types';

export const stagflation: CaseStudy = {
  id: 'stagflation',
  order: 13,
  title: '1970 年代滞胀',
  english: 'The Great Inflation',
  category: 'economics',
  era: '1965 — 1983',
  region: '美国 → 全球',
  accent: '#a7782f',
  hook: '通胀不是一张价格表。它是能源冲击、工资谈判、央行信誉和公众预期互相追赶的结果。',
  question: '为什么加息会先造成衰退，却可能是重新锚定通胀预期的代价？',
  summary:
    '1970 年代的美国同时经历高通胀、高失业和低增长。石油冲击改变了相对价格，工资与价格指数化让通胀更持久，而央行是否愿意承受衰退、把通胀压回去，决定了预期能否重新稳定。',
  thesis: '通胀的持续性不只取决于货币数量，也取决于人们相信央行会在多大程度上容忍痛苦。',
  quote: '当所有人都预期价格继续上涨，等待和犹豫本身就会让通胀更难结束。',
  isNew: true,
  entryNodeIds: ['stag-oil', 'stag-expect', 'stag-cred'],
  focusNodeId: 'stag-cred',
  lensIds: ['reflexivity', 'commitment', 'incentives', 'mismatch'],
  opportunityIds: ['opp-inflation-pricing'],
  sourceIds: ['fed-great-inflation', 'bls-cpi', 'fred-cpi'],
  facts: [
    {
      text: '美国 CPI 同比在 1980 年 3 月达到约 14.8% 的高点；整个 1970 年代的通胀长期高于此前二十年的水平。',
      sourceIds: ['bls-cpi', 'fred-cpi'],
    },
    {
      text: '1979 年保罗·沃尔克出任美联储主席后，联邦基金利率在 1981 年前后被推高到接近 20% 的区间；美国失业率在 1982 年末超过 10%。',
      sourceIds: ['fed-great-inflation'],
    },
    {
      text: '到 1980 年代中期，美国通胀回落到较低水平，但代价包括深度衰退、失业上升和拉美债务危机等外溢影响。',
      sourceIds: ['fed-great-inflation'],
    },
  ],
  nodes: [
    {
      id: 'stagflation',
      caseId: 'stagflation',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '1970 年代滞胀',
      summary: '高通胀与高失业同时出现，央行被迫重新建立信誉。',
      detail:
        '二战后美国长期接受“低通胀、低失业”的政策组合，但 1960 年代末的财政扩张、石油冲击、工资谈判和宽松货币政策逐渐改变了预期。到 1970 年代，通胀与失业不再呈现简单的此消彼长，政策制定者面对的是一个更棘手的预期问题。',
      boundary: '本案例以美国为主；不同国家的通胀成因、劳动制度和央行独立性差异很大。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fed-great-inflation', 'bls-cpi'],
    },
    {
      id: 'stag-oil',
      caseId: 'stagflation',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '供给冲击：油价改变了相对价格',
      summary: '石油价格骤升推高运输、化工与取暖成本，冲击总供给。',
      detail:
        '1973 年与 1979 年的石油冲击让能源成本大幅上升。企业面对更高的投入成本，既可能涨价，也可能减产；消费者面对更高的能源账单，实际购买力下降。供给冲击不能靠“多印钱”解决，扩张总需求反而可能让通胀更顽固。',
      boundary: '油价冲击的传导取决于能源强度、替代能源、汇率与劳动市场制度；不是每次油价上涨都会造成滞胀。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-great-inflation', 'eia-oil-prices'],
    },
    {
      id: 'stag-expect',
      caseId: 'stagflation',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '预期：通胀开始自我实现',
      summary: '工人、企业和消费者都预期价格继续上涨，工资与合同随之调整。',
      detail:
        '当人们相信通胀会持续，工会会要求更高工资，企业会把未来成本写进价格，家庭会提前购买。工资—价格螺旋不是每个人“贪心”，而是分散个体对同一预期的理性反应。预期一旦进入合同和工资，通胀就获得了惯性。',
      boundary: '预期能否自我实现，取决于央行信誉、劳动市场谈判结构和合同指数化程度。',
      counter: '在通胀预期稳定的经济体，能源冲击通常只造成一次性价格水平上升，而非持续通胀。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-great-inflation', 'bls-cpi'],
    },
    {
      id: 'stag-cred',
      caseId: 'stagflation',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '央行信誉：愿意承受多大痛苦',
      summary: '只有市场相信央行会坚持紧缩，通胀预期才可能真正下降。',
      detail:
        '如果央行一方面加息、另一方面又害怕衰退而迅速转向，公众会预期通胀迟早回来。可信的紧缩不只是提高利率，还包括对政治压力的抵抗、清晰的沟通和持续的行动。信誉是一种承诺装置：它让短期的痛苦换取长期的预期稳定。',
      boundary: '紧缩的可信度也受财政政策、金融稳定和外部冲击影响；过度紧缩可能引爆债务与银行危机。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-great-inflation'],
    },
    {
      id: 'stag-wage',
      caseId: 'stagflation',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '指数化：把临时通胀写进长期合同',
      summary: '工资、养老金和合同与 CPI 挂钩，让一次冲击更容易变成持续成本。',
      detail:
        '指数化在通胀高时保护购买力，但也把过去的通胀自动带入未来。企业将成本转嫁，工会争取补偿，政府支出随价格上升。每一轮指数化都降低了经济对通胀的敏感度，却提高了通胀的持续性。',
      boundary: '指数化本身不是错误；在低通胀、可信央行环境下，它可以是合理的保险。',
      evidence: 'mechanism',
      confidence: 'medium',
      sourceIds: ['fed-great-inflation'],
    },
    {
      id: 'stag-condition',
      caseId: 'stagflation',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：能源依赖与宽松财政',
      summary: '高能源强度、扩张性财政、宽松货币与劳动谈判共同放大了冲击。',
      detail:
        '1970 年代的美国经济对石油高度依赖，财政与货币政策在相当长时间里偏向扩张，劳动市场又有较强的工资谈判能力。这些条件让供给冲击更容易转化为广泛的成本上升，而不是局部涨价。',
      boundary: '当能源强度下降、央行独立性和财政纪律增强时，同样的油价冲击影响会不同。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fed-great-inflation'],
    },
    {
      id: 'stag-outcome',
      caseId: 'stagflation',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：深度紧缩换来通胀回落',
      summary: '高利率、衰退与失业最终压低了通胀，也重塑了央行独立性。',
      detail:
        '沃尔克时期的美联储把利率推到历史高位，通胀逐步回落，但美国经历了严重衰退，失业率超过 10%。拉美等新兴市场也因美元利率上升和债务成本上升受到冲击。通胀目标制与央行独立性在随后几十年成为主流制度安排。',
      boundary: '这是一次特定历史条件下的政策选择，不能简单复制为“加息总能无痛解决通胀”。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fed-great-inflation'],
    },
    {
      id: 'stag-risk',
      caseId: 'stagflation',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：紧缩的政治代价',
      summary: '衰退、失业与债务违约会迅速制造放弃紧缩的政治压力。',
      detail:
        '紧缩的收益要过一段时间才出现，而失业和破产是立刻可见的。政府、工会、企业和选民都会要求放松。若央行在通胀真正下降前转向，信誉会进一步受损，下一次反通胀需要更大代价。',
      boundary: '如果财政与金融系统有足够缓冲，紧缩的社会成本可以被分担；否则可能引发更深的危机。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fed-great-inflation'],
    },
    {
      id: 'stag-opp',
      caseId: 'stagflation',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：通胀下的定价与现金流',
      summary: '能否转嫁成本、缩短账期、锁定关键投入，比“涨价”本身更重要。',
      detail:
        '对企业和个人，通胀环境会重新分配购买力：有定价权、低负债、短账期和可变成本的企业更容易生存；固定收入、长期固定价格合同和高杠杆的主体更脆弱。机会可能来自帮小企业做成本转嫁、供应链替代、现金流压力测试和合同条款重谈。',
      boundary: '不是所有涨价都能被客户接受；价格管制、竞争和需求弹性会限制转嫁空间。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['fed-great-inflation'],
    },
  ],
  edges: [
    {
      id: 'stag-e1',
      caseId: 'stagflation',
      from: 'stagflation',
      to: 'stag-oil',
      label: '外部冲击',
      explanation: '石油价格骤升改变了能源、运输和化工的相对成本。',
      tone: 'cause',
    },
    {
      id: 'stag-e2',
      caseId: 'stagflation',
      from: 'stagflation',
      to: 'stag-expect',
      label: '预期进入工资与价格',
      explanation: '持续的通胀让工人和企业把未来涨价写进合同与定价。',
      tone: 'cause',
    },
    {
      id: 'stag-e3',
      caseId: 'stagflation',
      from: 'stag-oil',
      to: 'stag-wage',
      label: '成本推动工资谈判',
      explanation: '能源与生活成本上升，工会要求更高工资以维持购买力。',
      tone: 'cause',
    },
    {
      id: 'stag-e4',
      caseId: 'stagflation',
      from: 'stag-expect',
      to: 'stag-cred',
      label: '预期逼迫央行表态',
      explanation: '当预期脱锚，央行必须用更可信、更持久的紧缩来重新锚定。',
      tone: 'cause',
    },
    {
      id: 'stag-e5',
      caseId: 'stagflation',
      from: 'stag-wage',
      to: 'stag-outcome',
      label: '通胀惯性',
      explanation: '工资和合同指数化把一次冲击转化为持续的成本上升。',
      tone: 'cause',
    },
    {
      id: 'stag-e6',
      caseId: 'stagflation',
      from: 'stag-cred',
      to: 'stag-outcome',
      label: '紧缩压回预期',
      explanation: '高利率与持续紧缩提高借贷成本、压低需求，逐步改变定价预期。',
      tone: 'cause',
    },
    {
      id: 'stag-e7',
      caseId: 'stagflation',
      from: 'stag-condition',
      to: 'stag-outcome',
      label: '结构条件放大冲击',
      explanation: '能源依赖、财政扩张与工资谈判结构，让冲击更容易扩散。',
      tone: 'enable',
    },
    {
      id: 'stag-e8',
      caseId: 'stagflation',
      from: 'stag-outcome',
      to: 'stag-risk',
      label: '紧缩制造政治压力',
      explanation: '衰退与失业使政府和社会要求央行放松，威胁反通胀的可信度。',
      tone: 'risk',
    },
    {
      id: 'stag-e9',
      caseId: 'stagflation',
      from: 'stag-risk',
      to: 'stag-opp',
      label: '不确定性变成服务需求',
      explanation: '企业需要更频繁地重估成本、合同与现金流，专业服务因此有空间。',
      tone: 'enable',
    },
  ],
};
